/*
 * @Description: 数据格式化，用于模板展示
 * @Date: 2024-11-28 14:31:34
 */
import { isNil, isFinite, isFunction } from 'lodash';
import { useDateFormat } from '@vueuse/core';
import { isNumber } from '@/utils/is';
import { translator } from './';

/**
 * @description: 手机号脱敏
 * @param {string} phone
 * @return {*}
 */
export const maskPhone = phone => {
  return defaultFormat(phone, () => {
    return (phone || '').replace(/(\d{3})\d+(\d{4})/, '$1****$2');
  });
};

/**
 * @description: 身份证号脱敏
 * @param {string} idCard
 * @return {*}
 */
export const maskIdCard = (idCard: string) => {
  return defaultFormat(idCard, () => {
    return (idCard || '').replace(
      /(\d{3})(\d+)(\w{4})/,
      (_, $1, $2, $3) => $1 + $2.replaceAll(/\d/g, '*') + $3
    );
  });
};

/**
 * @description: 姓名脱敏
 * @param {string} name
 * @return {*}
 */
export const maskName = name => {
  if (!name) {
    return name;
  }
  if (name.length == 2) {
    return name.substring(0, 1) + '*';
  }
  return name.substring(0, 1) + '*' + name.charAt(name.length - 1);
};
/**
 * @description: 银行卡号脱敏
 * @param {string} bankCard
 * @return {*}
 */
export const maskBankCard = (bankCard: string): string => {
  if (!bankCard || bankCard.length <= 4) {
    return bankCard;
  }
  return bankCard.replace(/.(?=.{4})/g, '*');
};

/**
 * @description: 根据类型对输入进行脱敏处理
 * @param {MaskOptions} options type('name' | 'phone' | 'idCard'|"bankCard") val(any)
 * @return {string}
 */
export const maskAllMethod = ({ type, val }: MaskOptions): string => {
  switch (type) {
    case 'name':
      return maskName(val);
    case 'phone':
      return maskPhone(val);
    case 'idCard':
      return maskIdCard(val);
    case 'bankCard':
      return maskBankCard(val);
    default:
      throw new Error('请传入类型name | phone | idCard | bankCard');
  }
};
interface MaskOptions {
  type: 'name' | 'phone' | 'idCard' | 'bankCard';
  val: string;
}

/**
 * @description: 默认值过滤器
 */
export const defaultFormat = (value, fn?) => {
  if (isNil(value)) return '-';
  if (isFunction(fn)) return fn();
  return value;
};
/**
 * @description: 时间过滤器
 * @see — https://vueuse.org/useDateFormat
 * @param {Date} value
 * @param {string} format
 * @return {string}
 */
export const date: (
  value: string | number | Date,
  format?: string | 'date' | 'month' | 'datetime'
) => string = (value, format = 'YYYY-MM-DD HH:mm:ss') =>
  defaultFormat(value, () => {
    if (!value) return '-';

    // new Date解决ISO 8601 时间格式
    if (isNumber(+value) && !isNaN(+value)) {
      // 时间戳
      value = new Date(Math.round(Number(String(+value).padEnd(13, '0'))));
    } else {
      value = new Date(value);
    }
    if (isNaN(value as any)) return '-';

    enum formatEnum {
      date = 'YYYY-MM-DD',
      month = 'YYYY-MM',
      datetime = 'YYYY-MM-DD HH:mm:ss'
    }
    format = formatEnum[format] || format;

    return useDateFormat(value, format).value;
  });

/**
 * @description: 去除对象中的空属性
 * @param {object} obj
 * @return {*}
 */
export const removeEmpty = obj => {
  for (const key in obj) {
    if (obj[key] === '' || obj[key] === null || obj[key] === undefined) {
      delete obj[key];
    }
  }
  return obj;
};

/** 找到字典中对应值的label */
export function findDictLabel(
  dict: { label: string; value: string | number }[],
  value: string | number
) {
  if (value == null) return '-';
  const result = dict.find(item => item.value === value);
  return result ? result.label : '-';
}

/**
 * @description: 通过截取的方式设置小数的位数，不参与换算
 * @param {String|Number} value
 * @param {String} decimal 位数
 * @param {Boolean} toFixed 四舍五入
 * @return {String}
 */
export function fillDecimal(value, decimal, toFixed?) {
  return defaultFormat(value, () => {
    const _v = +value;
    if (!isFinite(_v)) return '-';
    if (toFixed) return _v.toFixed(decimal);
    let v = _v.toString();
    const index = v.indexOf('.');
    if (index !== -1) {
      v = v.substring(0, decimal + index + 1);
    } else {
      v = v.substring(0);
    }
    // toFixed防止异常情况，例如decimal与substring后的位数对不上，这种情况不会出现
    return parseFloat(v).toFixed(decimal);
  });
}

/**
 * @description: 分转元
 * @param {number} value 值如果异常则返回-，因为多用于展示
 * @param {string} suffix 后缀
 * @param {boolean} toFixed 是否四舍五入，false如果出现未除尽的情况1.33666/1.33111那么此处结果为1.33
 * @return {string}
 */
export const fen2yuan = (
  value: number | string,
  suffix = '',
  toFixed?
): string => {
  return defaultFormat(value, () => {
    const _v = +value;
    if (!isFinite(_v)) return '-';
    // fen2yuan未使用round，如果出现未除尽的情况1.33666/1.33111那么此处结果为1.33
    return fillDecimal(translator.fen2yuan(_v, toFixed), 2) + suffix;
  });
};
export const yuan2fen = (value: number) => translator.yuan2fen(value);

/**
 * @description: 数字类型格式化，限制输入只返回数字，返回格式为字符串的数字
 * @returns {*}
 */
export const formatValueToNumber = ({
  value,
  isInteger = false, // 是否是整数
  max = 100, // 最大值
  min = 0, // 最小值
  decimal = 2 // 小数点位数
}: {
  value: any;
  isInteger?: boolean;
  max?: number;
  min?: number;
  decimal?: number;
}) => {
  value = String(value).trim();
  if (value.startsWith(max)) return max;
  let formattedValue: any = '';

  value = value.replaceAll('。', '.'); // 替换中文逗号

  // 非纯数字的字符串
  if (isNaN(Number(value))) {
    // 如果是 1.2/[]\  这种的字符串取出数字部分
    const num = value.match(/\d+(\.\d+)?/)?.[0];
    formattedValue = isNaN(Number(num)) ? '' : num;
  }
  // 如果是 0212 这种数据返回 0
  else if (/^0\d+$/.test(value) && value !== '0') {
    formattedValue = 0;
  }
  // 输入值比限定最小值小，清空
  else if (+value < min) {
    formattedValue = '';
  }
  // 输入值比限定最大值大，截取
  else if (+value > max) {
    formattedValue = String(value).slice(0, String(value).length - 1);
  } else {
    formattedValue = value;
  }

  const nums = String(formattedValue).split('.');

  // 小数位数处理
  if (!isInteger && String(formattedValue).includes('.')) {
    formattedValue = nums[0] + '.' + nums[1].slice(0, decimal);
  }

  // 整数处理
  if (isInteger) formattedValue = nums[0];

  if (formattedValue < min) formattedValue = '';

  return formattedValue;
};
